import { Icon, useResponsiveValue } from '@monash/portal-react';
import { WidgetContext } from '@/components/providers/WidgetProvider';
import DraggableWindow from '@/components/ui/draggable-window/DraggableWindow';
import { MOBILE_RESPONSIVE } from '@/components/ui/main/Main';
import { format } from 'date-fns';
import { useContext } from 'react';
import c from './version-history-menu.module.scss';
import { calculateVersionArray } from '../utils/calculate-version-array';
import { ViewRestoreButton } from './view-restore-button/ViewRestoreButton';

const VersionHistory = ({
  versions,
  escapeFocusRef,
  selectedVersionIndex,
  setSelectedVersionIndex,
  setContent,
  updateDataByObject,
  willOverrideExistingVersion,
  setOverrideTimer,
}) => {
  const HEADING_TEXT = 'Version history';
  const responsiveSize = useResponsiveValue(MOBILE_RESPONSIVE);
  const isMobile = responsiveSize === 'S';
  const { editActiveWidget, editNotesVersions } = useContext(WidgetContext);

  const getTriggerRefPosition = () => {
    if (!escapeFocusRef?.current) return;
    const position = escapeFocusRef.current.getBoundingClientRect();
    return { x: position.right, y: position.bottom };
  };

  const onClose = () => {
    escapeFocusRef.current?.focus();
    editNotesVersions(null);
    setSelectedVersionIndex(0);
  };

  const view = (index) => {
    setSelectedVersionIndex(index);
  };

  const restore = (index) => {
    const restoredContent = versions[index];
    const versionArray = calculateVersionArray({
      noteContent: restoredContent,
      currentVersions: versions,
      overrideExistingVersion: false,
    });

    // Update firestore
    updateDataByObject({
      updates: {
        content: restoredContent,
        versions: versionArray,
      },
    });

    // Update editor locally
    setContent(restoredContent);

    willOverrideExistingVersion.current = true;
    setOverrideTimer();
    onClose();
  };

  return (
    <DraggableWindow
      fullScreen={isMobile}
      title={HEADING_TEXT}
      isShowing={editActiveWidget}
      focusTrapEnabled
      focusTrapSelectors="button:not([disabled]), input[type=text], input[type=url]"
      onClose={onClose}
      initialPosition={getTriggerRefPosition()}
      extraRootProps={{
        role: 'dialog',
        'aria-label': HEADING_TEXT,
      }}
      keyboardDismissalEnabled
    >
      <div className={c.versionHistory}>
        {versions?.map((version, index) => {
          const showButton = selectedVersionIndex !== 0 || index !== 0;
          const isSelected = selectedVersionIndex === index;
          const dateDisplay = format(version.timestamp, 'h:mmaaa, d MMM y');

          return (
            <div className={c.row} key={version.timestmap}>
              <div className={c.textWrapper}>
                {index === 0 && <div className={c.current}>Current</div>}
                <span>{dateDisplay}</span>

                {selectedVersionIndex === index && (
                  <span className={c.tick}>
                    <Icon.Check />
                  </span>
                )}
              </div>

              {showButton && (
                <ViewRestoreButton
                  view={view}
                  restore={restore}
                  isSelected={isSelected}
                  timestamp={version.timestamp}
                  index={index}
                />
              )}
            </div>
          );
        })}
      </div>
    </DraggableWindow>
  );
};

export default VersionHistory;
